import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../utils/apiService';
import { formatDate } from '../utils/dateUtils';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Stack,
  Menu,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { 
  ArrowBack as BackIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as DownloadIcon,
  Preview as PreviewIcon,
  FileDownload as ExportIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Style constants
const controlStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    height: 36,
  },
  '& .MuiInputBase-input': {
    py: 0.75,
    fontSize: '0.875rem'
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem'
  },
  '& .MuiSelect-select': {
    py: 0.75
  }
};

// Sample data for demonstration
const sampleData = {
  profitLoss: [
    { id: 1, date: '2023-01-01', description: 'Sales Revenue', amount: 10000, type: 'income' },
    { id: 2, date: '2023-01-01', description: 'Cost of Goods', amount: 5000, type: 'expense' },
    { id: 3, date: '2023-01-01', description: 'Operating Expenses', amount: 2000, type: 'expense' },
  ],
  balanceSheet: [
    { id: 1, account: 'Cash', amount: 5000, type: 'asset' },
    { id: 2, account: 'Accounts Receivable', amount: 3000, type: 'asset' },
    { id: 3, account: 'Accounts Payable', amount: 2000, type: 'liability' },
  ],
  taxReport: [
    { id: 1, date: '2023-01-01', invoice: 'INV-001', amount: 1000, vat: 150 },
    { id: 2, date: '2023-01-01', invoice: 'INV-002', amount: 2000, vat: 300 },
  ],
  taxDetails: [
    { id: 1, date: '2023-01-01', invoice: 'INV-001', customer: 'Customer A', amount: 1000, vat: 150, status: 'Paid' },
    { id: 2, date: '2023-01-01', invoice: 'INV-002', customer: 'Customer B', amount: 2000, vat: 300, status: 'Unpaid' },
  ],
};

const reportColumns = {
  profitLoss: [
    { field: 'date', headerName: 'Date', width: '15%' },
    { field: 'description', headerName: 'Description', width: '40%' },
    { field: 'amount', headerName: 'Amount', width: '20%', align: 'right' },
    { field: 'type', headerName: 'Type', width: '15%' },
  ],
  balanceSheet: [
    { field: 'account', headerName: 'Account', width: '40%' },
    { field: 'amount', headerName: 'Amount', width: '30%', align: 'right' },
    { field: 'type', headerName: 'Type', width: '20%' },
  ],
  taxReport: [
    { field: 'date', headerName: 'Date', width: '15%' },
    { field: 'invoice', headerName: 'Invoice', width: '20%' },
    { field: 'amount', headerName: 'Amount', width: '20%', align: 'right' },
    { field: 'vat', headerName: 'VAT', width: '15%', align: 'right' },
  ],
  taxDetails: [
    { field: 'date', headerName: 'Date', width: '15%' },
    { field: 'invoice', headerName: 'Invoice', width: '15%' },
    { field: 'customer', headerName: 'Customer', width: '25%' },
    { field: 'amount', headerName: 'Amount', width: '15%', align: 'right' },
    { field: 'vat', headerName: 'VAT', width: '15%', align: 'right' },
    { field: 'status', headerName: 'Status', width: '15%' },
  ],
  quotationReport: [
    { field: 'quotationNo', headerName: 'Quotation #', width: '12%' },
    { field: 'date', headerName: 'Date', width: '12%' },
    { field: 'customer', headerName: 'Customer', width: '20%' },
    { field: 'validUntil', headerName: 'Valid Until', width: '12%' },
    { field: 'amount', headerName: 'Amount', width: '13%', align: 'right' },
    { field: 'vat', headerName: 'VAT', width: '13%', align: 'right' },
    { field: 'total', headerName: 'Total', width: '13%', align: 'right' },
    { field: 'status', headerName: 'Status', width: '10%' },
  ],
};

const reportTitles = {
  profitLoss: 'Profit and Loss Report',
  balanceSheet: 'Balance Sheet Report',
  taxReport: 'VAT Summary Report',
  taxDetails: 'VAT Details Report',
  quotationReport: 'Quotation Report',
};

function ReportForm() {
  const { reportType } = useParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    months: [],
    status: '',
    search: '',
    customerId: '',
  });
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportOpen = Boolean(exportAnchorEl);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Fetch quotation report data
  const fetchQuotationReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.customerId) params.append('customerId', filters.customerId);
      if (filters.status) params.append('status', filters.status);
      if (filters.startDate) params.append('startDate', filters.startDate.toISOString());
      if (filters.endDate) params.append('endDate', filters.endDate.toISOString());
      
      const response = await api.get(`/reports/quotation-report?${params.toString()}`);
      
      if (response.data.success) {
        setReportData(response.data.data);
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Error fetching quotation report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load data when reportType is quotationReport
  useEffect(() => {
    if (reportType === 'quotationReport') {
      fetchQuotationReport();
    }
  }, [reportType]);

  // Reload data when filters change for quotation report
  const handleApplyFilters = () => {
    if (reportType === 'quotationReport') {
      fetchQuotationReport();
    }
  };

  const handleFilterChange = (field) => (event) => {
    setFilters({
      ...filters,
      [field]: event.target.value,
    });
  };

  const handleDateChange = (field) => (date) => {
    setFilters({
      ...filters,
      [field]: date,
    });
  };

  const handleMonthChange = (event) => {
    const {
      target: { value },
    } = event;
    setFilters({
      ...filters,
      months: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      months: [],
      status: '',
      search: '',
      customerId: '',
    });
    // Reload data after reset
    if (reportType === 'quotationReport') {
      setTimeout(() => fetchQuotationReport(), 100);
    }
  };

  const handleBack = () => {
    navigate('/reports');
  };

  const handleDownload = () => {
    // TODO: Implement download logic
    console.log('Downloading report');
  };

  const handlePreview = () => {
    // TODO: Implement preview logic
    console.log('Previewing report');
  };

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleExportFormat = (format) => {
    // TODO: Implement export logic for different formats
    console.log(`Exporting as ${format}`);
    handleExportClose();
  };

  const handlePrint = () => {
    // TODO: Implement print logic
    console.log('Printing report');
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          borderRadius: '4px',
          maxWidth: '1800px',
          margin: '0 auto',
          boxShadow: 'none',
          border: 'none'
        }}
      >
        {/* Header Section */}
        <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={handleBack}
              sx={{ mr: 0.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}
            >
              <BackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
                {reportTitles[reportType] || 'Report'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.875rem' }}>
                View and analyze your report data with customizable filters
              </Typography>
            </Box>
          </Box>
          <Box>
            <IconButton 
              onClick={handlePreview}
              sx={{ 
                mr: 0.5, 
                color: '#2196f3',
                border: '1px solid #2196f3',
                '&:hover': {
                  backgroundColor: 'rgba(33, 150, 243, 0.08)'
                }
              }}
              title="Preview"
            >
              <PreviewIcon />
            </IconButton>
            <IconButton 
              onClick={handleExportClick}
              sx={{ 
                mr: 0.5, 
                color: '#4caf50',
                border: '1px solid #4caf50',
                '&:hover': {
                  backgroundColor: 'rgba(76, 175, 80, 0.08)'
                }
              }}
              title="Export"
            >
              <ExportIcon />
            </IconButton>
            <Menu
              anchorEl={exportAnchorEl}
              open={exportOpen}
              onClose={handleExportClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={() => handleExportFormat('PDF')} sx={{ fontSize: '0.875rem' }}>
                Export as PDF
              </MenuItem>
              <MenuItem onClick={() => handleExportFormat('DOC')} sx={{ fontSize: '0.875rem' }}>
                Export as DOC
              </MenuItem>
              <MenuItem onClick={() => handleExportFormat('XLS')} sx={{ fontSize: '0.875rem' }}>
                Export as XLS
              </MenuItem>
            </Menu>
            <IconButton 
              onClick={handlePrint}
              sx={{ 
                mr: 0.5, 
                color: '#f44336',
                border: '1px solid #f44336',
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.08)'
                }
              }}
              title="Print"
            >
              <PrintIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Filter Section */}
        <Box 
          sx={{ 
            p: 0.5, 
            mb: 0.5,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, fontSize: '0.875rem' }}>
            Filters
          </Typography>
          <Grid container spacing={0.5} alignItems="center">
            {reportType === 'taxDetails' ? (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Months</InputLabel>
                  <Select
                    multiple
                    value={filters.months}
                    onChange={handleMonthChange}
                    label="Months"
                    sx={{
                      ...controlStyle,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: 0,
                      },
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Box
                            key={value}
                            sx={{
                              backgroundColor: 'rgba(67, 97, 238, 0.1)',
                              borderRadius: '4px',
                              padding: '2px 8px',
                              fontSize: '0.75rem',
                            }}
                          >
                            {value}
                          </Box>
                        ))}
                      </Box>
                    )}
                  >
                    {months.map((month) => (
                      <MenuItem
                        key={month}
                        value={month}
                        sx={{ fontSize: '0.875rem' }}
                      >
                        {month}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <>
                <Grid item xs={12} md={2}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={filters.startDate}
                      onChange={handleDateChange('startDate')}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true, 
                          margin: "dense",
                          size: "small",
                          sx: controlStyle
                        } 
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={2}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date"
                      value={filters.endDate}
                      onChange={handleDateChange('endDate')}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true, 
                          margin: "dense",
                          size: "small",
                          sx: controlStyle
                        } 
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </>
            )}
            <Grid item xs={12} md={reportType === 'taxDetails' ? 3 : 2}>
              <FormControl fullWidth margin="dense" size="small">
                <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={handleFilterChange('status')}
                  label="Status"
                  sx={{
                    ...controlStyle,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderRadius: 0,
                    },
                  }}
                >
                  {reportType === 'quotationReport' ? (
                    <>
                      <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All</MenuItem>
                      <MenuItem value="draft" sx={{ fontSize: '0.875rem' }}>Draft</MenuItem>
                      <MenuItem value="sent" sx={{ fontSize: '0.875rem' }}>Sent</MenuItem>
                      <MenuItem value="accepted" sx={{ fontSize: '0.875rem' }}>Accepted</MenuItem>
                      <MenuItem value="rejected" sx={{ fontSize: '0.875rem' }}>Rejected</MenuItem>
                      <MenuItem value="expired" sx={{ fontSize: '0.875rem' }}>Expired</MenuItem>
                    </>
                  ) : (
                    <>
                      <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All</MenuItem>
                      <MenuItem value="paid" sx={{ fontSize: '0.875rem' }}>Paid</MenuItem>
                      <MenuItem value="unpaid" sx={{ fontSize: '0.875rem' }}>Unpaid</MenuItem>
                      <MenuItem value="overdue" sx={{ fontSize: '0.875rem' }}>Overdue</MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={reportType === 'taxDetails' ? 3 : 3}>
              <TextField
                label="Search"
                value={filters.search}
                onChange={handleFilterChange('search')}
                fullWidth
                margin="dense"
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={controlStyle}
              />
            </Grid>
            <Grid item xs={12} md={reportType === 'taxDetails' ? 3 : 2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={handleResetFilters}
                sx={{ 
                  fontSize: '0.75rem',
                  flex: 0.5,
                  height: '36px',
                  minWidth: 'auto',
                  px: 1
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={<FilterIcon />}
                onClick={handleApplyFilters}
                sx={{ 
                  fontSize: '0.875rem',
                  flex: 1,
                  height: '36px'
                }}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Separator Line */}
        <Box 
          sx={{ 
            height: '1px',
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
            my: 2,
            mx: 0.5
          }} 
        />

        {/* Data Grid Section */}
        <Box 
          sx={{ 
            p: 0.5,
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {reportColumns[reportType]?.map((column) => (
                    <TableCell
                      key={column.field}
                      align={column.align || 'left'}
                      sx={{ 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        py: 0.5,
                        backgroundColor: '#f8faff',
                        borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
                      }}
                    >
                      {column.headerName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={reportColumns[reportType]?.length || 1} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2">Loading...</Typography>
                    </TableCell>
                  </TableRow>
                ) : (reportType === 'quotationReport' ? reportData : sampleData[reportType])?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={reportColumns[reportType]?.length || 1} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">No data found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (reportType === 'quotationReport' ? reportData : sampleData[reportType])?.map((row) => (
                    <TableRow 
                      key={row.id}
                      sx={{ 
                        '&:last-child td': { borderBottom: '1px solid rgba(0, 0, 0, 0.12)' },
                        '& td': { 
                          fontSize: '0.875rem',
                          py: 0.5,
                          border: '1px solid rgba(0, 0, 0, 0.12)',
                        }
                      }}
                    >
                      {reportColumns[reportType]?.map((column) => (
                        <TableCell
                          key={column.field}
                          align={column.align || 'left'}
                        >
                          {column.field === 'date' || column.field === 'validUntil' 
                            ? formatDate(row[column.field])
                            : (column.align === 'right' && typeof row[column.field] === 'number')
                            ? `SAR ${row[column.field].toFixed(2)}`
                            : row[column.field]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    </Container>
  );
}

export default ReportForm; 